import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Stepper from './Stepper';
import ReservationSummary from './ReservationSummary';
import StepNavigation from './StepsNavigation';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useReservation } from '../components/ReservationContext';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';

export default function BookingLayout({ children }) {
  const location = useLocation();
  const { reservation } = useReservation();

  const isConfirmationPage = location.pathname === '/confirmation';
  const [showSummary, setShowSummary] = useState(false);
  const [stepNavigationProps, setStepNavigationProps] = useState({});

  // Clone children to pass setStepNavigationProps function as prop
  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child, { setStepNavigationProps })
      : child
  );

  const { rooms = [], checkIn, checkOut, totalBeforeTax = 0 } = reservation;
  const totalRooms = rooms.reduce((sum, room) => sum + room.count, 0);

  useEffect(() => {
    if (!showSummary) return;
    const handleScroll = () => setShowSummary(false);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showSummary]);

  return (
    <>
      {!isConfirmationPage && <Stepper />}

      <div className="container-fluid pb-5">
        <div className="row">
          <main className={`col-md-${isConfirmationPage ? '12' : '8'} mx-auto`}>
            {childrenWithProps}
          </main>

          {/* Desktop sidebar summary */}
          {!isConfirmationPage && (
            <aside className="col-md-4 d-none d-md-block">
              <div className="position-sticky top-0" style={{ zIndex: 1020 }}>
                <ReservationSummary />
              </div>
            </aside>
          )}
        </div>

        {/* Mobile combined bottom bar for summary toggle + step navigation */}
        {!isConfirmationPage && (
          <MobileCombinedBottomBar
            showSummary={showSummary}
            setShowSummary={setShowSummary}
            checkIn={checkIn}
            checkOut={checkOut}
            totalRooms={totalRooms}
            totalBeforeTax={totalBeforeTax}
            stepNavigationProps={stepNavigationProps}
          />
        )}

        {/* Desktop fixed bottom step navigation only */}
        {!isConfirmationPage && (
          <div
            className="position-fixed bottom-0 start-0 end-0 bg-white border-top pt-2 pb-3 d-none d-md-block"
            style={{
              zIndex: 1040,
              margin: '0 auto',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            }}
          >
            <StepNavigation {...stepNavigationProps} />
          </div>
        )}
      </div>
    </>
  );
}

function MobileCombinedBottomBar({
  showSummary,
  setShowSummary,
  checkIn,
  checkOut,
  totalRooms,
  totalBeforeTax,
  stepNavigationProps,
}) {
  const { onBack, onNext, canGoBack, canGoNext, currentStep } = stepNavigationProps;

  return (
    <div className="d-md-none">
      <div
        className="fixed-bottom bg-white border-top shadow-sm"
        style={{
          zIndex: 1050,
          maxWidth: '720px',
          margin: '0 auto',
          left: 0,
          right: 0,
          padding: '0.25rem 1rem',
        }}
      >
        {/* Top bar: Back button, Continue text, price and toggle */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 bg-light rounded"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowSummary((prev) => !prev)}
        >

<div
  className="text-muted d-flex flex-column"
  style={{
    fontSize: '0.9rem',
    userSelect: 'none',
    lineHeight: '1.2'
  }}
>
  {/* LINE 1 — Dates + Nights */}
  <div style={{ whiteSpace: 'nowrap' }}>
    {checkIn && checkOut
      ? `${dayjs(checkIn).format('MMM D')} → ${dayjs(checkOut).format('MMM D, YYYY')}
        `
      : `----/--  → ---/--/----`}
  </div>

  {/* LINE 2 — Rooms + Price */}
  <div
    className="d-flex justify-content-between align-items-center mt-1"
    style={{ whiteSpace: 'nowrap' }}
  >
    <span>
      🛏 {totalRooms} room{totalRooms !== 1 ? 's' : ''}
    </span>


  </div>
</div>


          <div className="d-flex align-items-center gap-2">
            
    <span className="fw-semibold">
      <span
        style={{
          fontSize: '0.65em',
          verticalAlign: 'text-top',
          color: '#6c757d',
          marginRight: '2px'
        }}
      >
        CAD
      </span>
      {totalBeforeTax.toFixed(2)}
    </span>
            {/* Toggle icon */}
            {showSummary ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>

        </div>

        {/* Expanded reservation summary + step navigation */}
        {showSummary && (
          <div className="pt-2 pb-2">
            <ReservationSummary />
            <div className="mt-3">
            </div>
          </div>
        )}

        {/* When collapsed show only step navigation below the bar */}
        {!showSummary && (
          <div className="pt-2">
            <StepNavigation {...stepNavigationProps} />
          </div>
        )}
      </div>
    </div>
  );
}
